import { useState, useMemo } from "react";
import type { Habit } from "../../../shared/types";
import { useHabits } from "../hooks/use-habits";
import { getToday, getWeekDays, addDays } from "../../../shared/types/date-helpers";
import { HabitForm } from "./HabitForm";
import { HabitDailyView } from "./HabitDailyView";
import { HabitWeeklyView } from "./HabitWeeklyView";
import { HabitEmptyState } from "./HabitEmptyState";
import { HabitEditSheet } from "./HabitEditSheet";
import type { useAppState } from "../../../hooks/useAppState";

type ViewMode = "daily" | "weekly";

const VIEW_MODES: { value: ViewMode; label: string }[] = [
  { value: "daily", label: "Quotidien" },
  { value: "weekly", label: "Semaine" },
];

interface HabitsViewProps {
  readonly appState: ReturnType<typeof useAppState>;
}

export function HabitsView({ appState }: HabitsViewProps) {
  const { habits, addHabit, deleteHabit, toggleHabitDate, getStreak, getBestStreak, updateHabit } =
    useHabits();

  const categoryFilter = appState.activeFilter;

  // Filtrer les habitudes par catégorie
  const filteredHabits = useMemo(() => {
    if (categoryFilter === "all") return habits;
    return habits.filter((h) => h.category === categoryFilter);
  }, [habits, categoryFilter]);

  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const [editingHabit, setEditingHabit] = useState<Habit | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  const handleEditHabit = (habit: Habit) => {
    setEditingHabit(habit);
  };

  const today = getToday();
  const referenceDate = addDays(today, weekOffset * 7);
  const weekDays = useMemo(() => getWeekDays(referenceDate), [referenceDate]);

  return (
    <div className="flex flex-col gap-3 py-6">
      {/* Formulaire d'ajout toujours visible */}
      <HabitForm
        categoryFilter={categoryFilter}
        onAdd={addHabit}
      />

      {filteredHabits.length === 0 ? (
        <HabitEmptyState categoryFilter={categoryFilter} />
      ) : (
        <>
          {/* Switcher de vue pleine largeur */}
          <div className="flex w-full rounded-lg overflow-hidden" role="tablist" data-testid="habit-view-toggle">
        {VIEW_MODES.map((mode) => (
          <button
            key={mode.value}
            type="button"
            role="tab"
            aria-selected={viewMode === mode.value}
            onClick={() => setViewMode(mode.value)}
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              viewMode === mode.value
                ? "bg-accent text-white"
                : "bg-surface-alt text-ink-muted hover:text-ink"
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

          {viewMode === "daily" ? (
            <HabitDailyView
              habits={filteredHabits}
              today={today}
              getStreak={getStreak}
              getBestStreak={getBestStreak}
              onToggle={toggleHabitDate}
              onEdit={handleEditHabit}
            />
          ) : (
            <HabitWeeklyView
              habits={filteredHabits}
              weekDays={weekDays}
              onToggle={toggleHabitDate}
              onEdit={handleEditHabit}
              onPrevWeek={() => setWeekOffset((v) => v - 1)}
              onNextWeek={() => setWeekOffset((v) => v + 1)}
            />
          )}
        </>
      )}

      <HabitEditSheet
        habit={editingHabit}
        onClose={() => setEditingHabit(null)}
        onUpdate={updateHabit}
        onDelete={deleteHabit}
      />
    </div>
  );
}
